import { ICommandBarItemProps, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { IPSpinner } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { clearQuickFilter, QuickFilterContext } from 'components/Filter/QuickFilter';
import { getSearchableMenuProps } from 'components/Utils/SearchableMenuList';
import { editIcon, eventIcon, groupIcon, removeFilterIcon, taskIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import { PermissionTypes } from 'models/auth/rolePermission';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskKinds } from 'models/tasks/taskType';
import { hasUserFeatureGenericManager, hasUserRolePermission } from 'services/Auth/featurePermissions';
import { addDateTimeYears, getAuditStartForSetting, toLocaleDateShort } from 'utils/datetime';
import { sortOnString } from 'utils/sorting';
import { TaskViewScheduleLevel, TaskViewType } from './TasksView';

//
// Button planning level
//
interface ITasksViewButtonScheduleLevelProps {
  onSelectLevel: (level: number) => void;
  disabled: boolean | undefined;
  appContext: IAppContext;
  clearQuickFilter: () => void;
}

export const getCommandBarItemsScheduleLevel = (
  level: TaskViewScheduleLevel,
  props: ITasksViewButtonScheduleLevelProps,
  t: TFunction<string[]>,
): ICommandBarItemProps => {
  const levelMenuProps: IContextualMenuProps = {
    items: [
      {
        key: 'header1',
        text: t('tasks:TaskView.Commands.ScheduleLevel.Main'),
        itemType: IPContextualMenuItemType.Header,
      },
      {
        key: 'year',
        checked: level === TaskViewScheduleLevel.Year,
        text: t('tasks:TaskView.Commands.ScheduleLevel.Year'),
        iconProps: { iconName: 'CalendarAgenda' },
        onClick: () => props.onSelectLevel(TaskViewScheduleLevel.Year),
      },
      {
        key: 'quarter',
        checked: level === TaskViewScheduleLevel.Quarter,
        text: t('tasks:TaskView.Commands.ScheduleLevel.Quarter'),
        iconProps: { iconName: 'CalendarDay' },
        onClick: () => props.onSelectLevel(TaskViewScheduleLevel.Quarter),
      },
      {
        key: 'month',
        checked: level === TaskViewScheduleLevel.Month,
        text: t('tasks:TaskView.Commands.ScheduleLevel.Month'),
        iconProps: { iconName: 'CalendarWeek' },
        onClick: () => props.onSelectLevel(TaskViewScheduleLevel.Month),
      },
      {
        key: 'header2',
        text: t('translation:General.Button.View'),
        itemType: IPContextualMenuItemType.Header,
      },
      {
        key: 'resetQuickFilters',
        text: t('translation:General.Button.ResetQuickFilter'),
        iconProps: removeFilterIcon,
        onClick: () => {
          clearQuickFilter(QuickFilterContext.scheduleTasks, props.appContext);
          props.clearQuickFilter();
        },
      },
    ],
  };

  const getMainText = (): string => {
    let subText: string = '';
    switch (level) {
      case TaskViewScheduleLevel.Year:
        subText = t('tasks:TaskView.Commands.ScheduleLevel.Year');
        break;
      case TaskViewScheduleLevel.Quarter:
        subText = t('tasks:TaskView.Commands.ScheduleLevel.Quarter');
        break;
      case TaskViewScheduleLevel.Month:
        subText = t('tasks:TaskView.Commands.ScheduleLevel.Month');
        break;
    }

    return t('tasks:TaskView.Commands.ScheduleLevel.Main') + ' : ' + subText;
  };

  return {
    className: 'isoplanner-annualplan-schedulelevel',
    key: 'level',
    disabled: props.disabled,
    text: getMainText(),
    iconProps: { iconName: 'CalendarSettings' },
    subMenuProps: levelMenuProps,
  };
};

//
// Button audit cycle
//
interface ITasksViewButtonAuditYearProps {
  onSelectYear: (year: number) => void;
  disabled: boolean | undefined;
  auditDateSetting: string;
}

const yearStartsInJan = (auditDateSetting?: string): boolean => {
  if (!auditDateSetting) return false;
  const month = getAuditStartForSetting(1, auditDateSetting).getMonth();

  return month === 0;
};

const getYearText = (yearIdx: number, props: ITasksViewButtonAuditYearProps, t: TFunction<string[]>) => {
  if (yearStartsInJan(props.auditDateSetting)) {
    return t('tasks:TaskView.Commands.AuditYear.ItemStartInJan', { year: yearIdx });
  } else {
    return t('tasks:TaskView.Commands.AuditYear.Item', { year: yearIdx, nextYear: yearIdx + 1 });
  }
};

const getYearItems = (
  selectedYear: number,
  props: ITasksViewButtonAuditYearProps,
  t: TFunction<string[]>,
): IContextualMenuItem[] => {
  return [
    {
      key: 'header1',
      text: t('tasks:TaskView.Commands.AuditYear.HeaderPrevNext'),
      itemType: IPContextualMenuItemType.Header,
    },
    {
      key: 'yearMinus1' + (selectedYear - 1).toString(),
      text: getYearText(selectedYear - 1, props, t),
      canCheck: true,
      checked: false,
      onClick: () => props.onSelectYear(selectedYear - 1),
    },
    {
      key: 'yearCurrent' + selectedYear.toString(),
      text: getYearText(selectedYear, props, t),
      canCheck: true,
      checked: true,
      onClick: () => props.onSelectYear(selectedYear),
    },
    {
      key: 'yearPlus1' + (selectedYear + 1).toString(),
      text: getYearText(selectedYear + 1, props, t),
      canCheck: true,
      checked: false,
      onClick: () => props.onSelectYear(selectedYear + 1),
    },
    {
      key: 'header2',
      text: t('tasks:TaskView.Commands.AuditYear.HeaderAll'),
      itemType: IPContextualMenuItemType.Header,
    },
  ];
};

export const getCommandBarItemsAuditYear = (
  year: number,
  props: ITasksViewButtonAuditYearProps,
  t: TFunction<string[]>,
  isUpdating?: boolean,
): ICommandBarItemProps => {
  const currentYear = new Date().getFullYear();
  const yearMenuProps: IContextualMenuProps = {
    directionalHint: IPDirectionalHint.bottomLeftEdge,
    directionalHintFixed: true,
    items: [
      ...getYearItems(year, props, t),
      ...Array.from({ length: 21 }, (_, i) => {
        const yearIdx: number = i + currentYear - 10;

        return {
          key: 'year' + yearIdx.toString(),
          text: getYearText(yearIdx, props, t),
          canCheck: true,
          checked: yearIdx === year,
          onClick: () => props.onSelectYear(yearIdx),
        };
      }),
    ],
    calloutProps: {
      calloutMaxHeight: 300,
    },
  };

  const getMainText = (): string => {
    const auditStart = getAuditStartForSetting(year, props.auditDateSetting);
    const auditYearStartString = toLocaleDateShort(auditStart);
    const auditYearEndString = toLocaleDateShort(addDateTimeYears(auditStart, 1));

    return t('tasks:TaskView.Schedule.Columns.PeriodInfo', {
      start: auditYearStartString,
      end: auditYearEndString,
    });
  };

  return {
    className: 'isoplanner-annualplan-audityear',
    key: 'audityear',
    disabled: props.disabled || isUpdating,
    text: getMainText(),
    iconProps: { iconName: 'Calendar' },
    onRenderIcon: !isUpdating
      ? undefined
      : () => {
          return <IPSpinner size={IPSpinnerSize.medium} />;
        },
    subMenuProps: yearMenuProps,
  };
};

//
// Button group by
//
interface ITasksViewButtonGroupByProps {
  onGroupBy: (field: 'state' | 'assignee') => void;
  disabled: boolean | undefined;
}

export const getCommandBarItemsGroupBy = (
  field: 'state' | 'assignee',
  props: ITasksViewButtonGroupByProps,
  t: TFunction<string[]>,
): ICommandBarItemProps => {
  const groupByMenuProps: IContextualMenuProps = {
    items: [
      {
        key: 'state',
        checked: field === 'state',
        text: t('tasks:TaskView.Commands.GroupBy.Status'),
        iconProps: { iconName: 'StatusCircleRing' },
        onClick: () => props.onGroupBy('state'),
      },
      {
        key: 'assignee',
        checked: field === 'assignee',
        text: t('tasks:TaskView.Commands.GroupBy.Assignee'),
        iconProps: groupIcon,
        onClick: () => props.onGroupBy('assignee'),
      },
    ],
  };

  const getMainText = (): string => {
    let subText: string = '';
    switch (field) {
      case 'state':
        subText = t('tasks:TaskView.Commands.GroupBy.Status');
        break;
      case 'assignee':
        subText = t('tasks:TaskView.Commands.GroupBy.Assignee');
        break;
    }

    return t('tasks:TaskView.Commands.GroupBy.Main') + ' : ' + subText;
  };

  return {
    key: 'groupBy',
    disabled: props.disabled,
    text: getMainText(),
    iconProps: { iconName: 'GroupList' },
    subMenuProps: groupByMenuProps,
  };
};

//
// Button switch view
//
interface ITasksViewButtonSwitchViewProps {
  onSwitchView: (viewType: TaskViewType) => void;
  disabled: boolean | undefined;
}

export const getCommandBarItemsSwitchView = (
  selectedViewType: TaskViewType,
  props: ITasksViewButtonSwitchViewProps,
  t: TFunction<string[]>,
  extraItems?: IContextualMenuItem[],
): ICommandBarItemProps => {
  if (extraItems && extraItems.length > 0) {
    extraItems.push({
      key: 'div1',
      text: '-',
      itemType: IPContextualMenuItemType.Divider,
    });
  }

  const viewTypeMenuProps: IContextualMenuProps = {
    items: [
      ...(extraItems ?? []),
      {
        key: 'list',
        checked: selectedViewType === TaskViewType.List,
        text: t('tasks:TaskView.Commands.View.List'),
        iconProps: { iconName: 'BulletedList' },
        onClick: () => props.onSwitchView(TaskViewType.List),
      },
      {
        key: 'board',
        checked: selectedViewType === TaskViewType.Board,
        text: t('tasks:TaskView.Commands.View.Board'),
        iconProps: { iconName: 'BacklogBoard' },
        onClick: () => props.onSwitchView(TaskViewType.Board),
      },
      {
        key: 'planning',
        checked: selectedViewType === TaskViewType.Schedule,
        text: t('tasks:TaskView.Commands.View.Schedule'),
        iconProps: { iconName: 'CalendarAgenda' },
        onClick: () => props.onSwitchView(TaskViewType.Schedule),
      },
      {
        key: 'timeline',
        checked: selectedViewType === TaskViewType.Timeline,
        text: t('tasks:TaskView.Commands.View.Timeline'),
        iconProps: { iconName: 'Timeline' },
        onClick: () => props.onSwitchView(TaskViewType.Timeline),
      },
    ],
  };

  const getMainText = (): string => {
    let subText: string = '';
    switch (selectedViewType) {
      case TaskViewType.Board:
        subText = t('tasks:TaskView.Commands.View.Board');
        break;
      case TaskViewType.Schedule:
        subText = t('tasks:TaskView.Commands.View.Schedule');
        break;
      case TaskViewType.List:
        subText = t('tasks:TaskView.Commands.View.List');
        break;
      case TaskViewType.Timeline:
        subText = t('tasks:TaskView.Commands.View.Timeline');
        break;
    }

    return t('tasks:TaskView.Commands.View.Main') + ' : ' + subText;
  };

  return {
    className: 'isoplanner-taskview-switchview',
    key: 'switchView',
    disabled: props.disabled,
    text: getMainText(),
    iconProps: { iconName: 'View' },
    subMenuProps: viewTypeMenuProps,
  };
};

//
// Button new task/event
//
interface ITasksViewButtonAddTaskEventProps {
  onAdd: () => void;
  onAddEvent?: () => void;
  onAddExistingTask?: () => void;
  allowAddExistingTask?: boolean;
  disabled?: boolean | undefined;
  className?: string;
}

const getTemplates = (
  props: ITasksViewButtonAddTaskEventProps,
  onClick: (template: Task) => void,
  onNavigateToTemplates: () => void,
  appContext: IAppContext,
  t: TFunction<string[]>,
  navigateExternal?: boolean,
): IContextualMenuProps => {
  const items: IContextualMenuItem[] = [];

  if (!navigateExternal && hasUserFeatureGenericManager(appContext)) {
    items.push({
      key: 'manage-templates',
      iconProps: editIcon,
      text: t('task:TopTasks.NoTemplateItems'),
      onClick: () => {
        onNavigateToTemplates();
      },
    });
    items.push({
      key: 'h1',
      itemType: IPContextualMenuItemType.Divider,
      text: '-',
    });
  }

  const output: IContextualMenuProps = {
    items,
    directionalHint: IPDirectionalHint.bottomLeftEdge,
    directionalHintFixed: true,
    ...getSearchableMenuProps({ pinnedKeys: ['manage-templates'] }),
  };

  const templates = appContext.globalDataCache.templates.items.filter(
    (t) => !t.isCompleted() && t.taskKind === TaskKinds.None && t.isTemplate(),
  );
  templates.sort((a, b) => sortOnString(a.name, b.name));

  if (templates && templates.length > 0) {
    output.items.push(
      ...templates.map((_itm: Task) => {
        return {
          key: `${_itm.taskId}`,
          text: `${_itm.name}`,
          iconProps: _itm.taskType === TaskTypes.Template ? taskIcon : eventIcon,
          onClick: () => onClick(_itm),
        };
      }),
    );
  } else {
    output.items.push({
      key: 'no-items',
      text: t('task:TopTasks.NoTemplateItemsUser'),
    });
  }

  return output;
};

export const getCommandBarItemsAddTaskEvent = (
  props: ITasksViewButtonAddTaskEventProps,
  appContext: IAppContext,
  t: TFunction<string[]>,
): ICommandBarItemProps => {
  return {
    className: props.className,
    itemProps: {
      classNames: {
        splitPrimary: props.className ? `${props.className}-split` : undefined,
      },
    },
    key: 'add',
    split: props.allowAddExistingTask || props.onAddEvent !== undefined,
    disabled: props.disabled || !hasUserRolePermission(appContext, PermissionTypes.CreateTask),
    text: t('tasks:TaskView.Commands.Add'),
    iconProps: { iconName: 'Add' },
    onClick: () => props.onAdd(),
    subMenuProps:
      props.allowAddExistingTask || props.onAddEvent
        ? {
            items: [
              ...(props.onAddEvent
                ? [
                    {
                      key: 'add-event',
                      text: t('tasks:Types.Event'),
                      iconProps: eventIcon,
                      onClick: () => props.onAddEvent?.(),
                    },
                  ]
                : []),
              ...(props.allowAddExistingTask
                ? [
                    {
                      key: 'add-existing',
                      text: t('tasks:TaskView.Commands.AddExistingTask'),
                      iconProps: { iconName: 'OpenFile' },
                      onClick: () => props.onAddExistingTask?.(),
                    },
                  ]
                : []),
            ],
          }
        : undefined,
  };
};

//
// Button add task from template
//
interface ITasksViewButtonAddFromTemplateProps {
  onAdd: (template?: Task) => void;
  onNavigateToTemplates: () => void;
  disabled?: boolean | undefined;
  className?: string;
}

export const getCommandBarItemsAddFromTemplate = (
  props: ITasksViewButtonAddFromTemplateProps,
  appContext: IAppContext,
  t: TFunction<string[]>,
  navigateExternal?: boolean,
): ICommandBarItemProps => {
  return {
    key: 'add-template',
    disabled: props.disabled || !hasUserRolePermission(appContext, PermissionTypes.CreateTask),
    text: t('tasks:TaskView.Commands.AddFromTemplate'),
    iconProps: { iconName: 'AddToShoppingList' },
    subMenuProps: getTemplates(props, props.onAdd, props.onNavigateToTemplates, appContext, t, navigateExternal),
  };
};
